import { query, run, isNative } from './sqlite-store';
import { offlineLog } from './log-buffer';

export interface FormDraft {
  templateId: string;
  workOrderId: string;
  userId: string;
  /** The complete form response object. */
  response: Record<string, unknown>;
  lastEditedAt: number;
}

const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

function draftKey(templateId: string, workOrderId: string, userId: string): string {
  return `${templateId}:${workOrderId}:${userId}`;
}

/** Save a draft (immediate write to SQLite). */
export async function saveDraft(draft: FormDraft): Promise<void> {
  if (!isNative()) return;
  await run(
    `INSERT OR REPLACE INTO drafts (template_id, work_order_id, user_id, response, last_edited_at)
     VALUES (?, ?, ?, ?, ?)`,
    [draft.templateId, draft.workOrderId, draft.userId, JSON.stringify(draft.response), draft.lastEditedAt],
  );
}

/**
 * Save a draft with 250ms debounce. Multiple rapid calls for the same
 * (template, WO, user) are coalesced into a single write.
 */
export function saveDraftDebounced(draft: FormDraft): void {
  if (!isNative()) return;
  const key = draftKey(draft.templateId, draft.workOrderId, draft.userId);
  const existing = debounceTimers.get(key);
  if (existing) clearTimeout(existing);
  debounceTimers.set(
    key,
    setTimeout(() => {
      debounceTimers.delete(key);
      saveDraft(draft).catch((err) =>
        offlineLog.error(`Draft save failed: ${err instanceof Error ? err.message : String(err)}`),
      );
    }, 250),
  );
}

/** Load a draft if one exists. */
export async function loadDraft(
  templateId: string,
  workOrderId: string,
  userId: string,
): Promise<FormDraft | null> {
  if (!isNative()) return null;
  const rows = await query<{
    template_id: string;
    work_order_id: string;
    user_id: string;
    response: string;
    last_edited_at: number;
  }>(
    `SELECT * FROM drafts WHERE template_id = ? AND work_order_id = ? AND user_id = ?`,
    [templateId, workOrderId, userId],
  );
  if (rows.length === 0) return null;
  const row = rows[0];
  return {
    templateId: row.template_id,
    workOrderId: row.work_order_id,
    userId: row.user_id,
    response: JSON.parse(row.response),
    lastEditedAt: row.last_edited_at,
  };
}

/** Delete a draft (called after server-acknowledged submission). */
export async function deleteDraft(
  templateId: string,
  workOrderId: string,
  userId: string,
): Promise<void> {
  if (!isNative()) return;
  await run(
    `DELETE FROM drafts WHERE template_id = ? AND work_order_id = ? AND user_id = ?`,
    [templateId, workOrderId, userId],
  );
  offlineLog.info(`Draft deleted: ${templateId}/${workOrderId}`);
}

/** List all drafts for a user (for the diagnostic sheet). */
export async function listDrafts(userId: string): Promise<FormDraft[]> {
  if (!isNative()) return [];
  const rows = await query<{
    template_id: string;
    work_order_id: string;
    user_id: string;
    response: string;
    last_edited_at: number;
  }>(
    `SELECT * FROM drafts WHERE user_id = ? ORDER BY last_edited_at DESC`,
    [userId],
  );
  return rows.map((row) => ({
    templateId: row.template_id,
    workOrderId: row.work_order_id,
    userId: row.user_id,
    response: JSON.parse(row.response),
    lastEditedAt: row.last_edited_at,
  }));
}
